<script>
import AdsPostpaidPaymentMethod from './components/ads-postpaid-payment-method.vue'
import AdsPostpaidPaymentSuccess from './components/ads-postpaid-payment-success.vue'
import AdsPostpaidPaymentFailure from './components/ads-postpaid-payment-failure.vue'
import { syncPostpaidPayment } from '@/api/ads'

const waitFor = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
const SUCCESS_POLLING_TIME = 60000 // ms

export default {
  name: 'AdsPostpaidResponse',
  components: {
    AdsPostpaidPaymentMethod,
    AdsPostpaidPaymentSuccess,
    AdsPostpaidPaymentFailure,
  },
  data() {
    return {
      isLoading: false,
      isSuccess: false,
      postpaidStatus: null,
    }
  },
  computed: {
    responseType() {
      return this.$route.meta.responseType
    },
  },
  created() {
    // Remove any params that we got when redirect back from Checkout site
    const url = new URL(window.location)
    const searchParams = new URLSearchParams(window.location.search)
    // the callback url should be like this for success case
    // web_url/?invoice_id=XXXX&payment_id=YYYY&cko-session-id=ZZZZ#/ads/postpaid/success
    if (url.searchParams.size > 0) {
      url.search = ''

      const invoiceId = searchParams.get('invoice_id')
      const paymentId = searchParams.get('payment_id')

      if (invoiceId && paymentId) {
        this.syncPayment(invoiceId, paymentId)
      }

      // expect the URL to be like this web_url/#/ads/postpaid/success?invoice_id=XXXX&payment_id=YYYY
      window.history.replaceState(
        {},
        document.title,
        url.toString() + '?' + searchParams.toString()
      )
    }

    const { invoice_id: invoiceId, payment_id: paymentId } =
      this.$route.query || {}

    if (invoiceId && paymentId && this.responseType === 'success') {
      this.syncPayment(invoiceId, paymentId)
    }
  },
  methods: {
    async syncPayment(invoiceId, paymentId) {
      try {
        this.isLoading = true
        const startTime = Date.now()

        while (true) {
          const { data } = await syncPostpaidPayment({ invoiceId, paymentId })

          const { data: postpaidStatus } = data

          // Check if the process has been checking for more than SUCCESS_POLLING_TIME
          // if so => stop and show message that payment still processing
          const timePassedSinceStart = Date.now() - startTime
          if (timePassedSinceStart > SUCCESS_POLLING_TIME) {
            this.postpaidStatus = 'pending'
            break
          }

          // If Pending => wait for 1000 ms then process to get status again
          // unless it pass the SUCCESS_POLLING_TIME
          if ((postpaidStatus.status || '').toLowerCase() === 'pending') {
            await waitFor(1000)
            continue
          }

          // Should be the case Success, Expired or Declined if reach here
          this.postpaidStatus = (postpaidStatus.status || '').toLowerCase()
          break
        }
      } catch {
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<template>
  <div class="ads-postpaid-response ads-page">
    <main>
      <template v-if="responseType === 'pay-now'">
        <AdsPostpaidPaymentMethod />
      </template>
      <div v-if="isLoading" class="payment-processing">
        <i class="el-icon-loading" />
        <p>{{ $t('ads_top_up_processing_message') }}</p>
      </div>
      <template v-if="!isLoading">
        <AdsPostpaidPaymentSuccess
          v-if="responseType === 'success' && postpaidStatus === 'success'"
        />
        <AdsPostpaidPaymentFailure v-if="responseType === 'error'" />
        <template v-if="postpaidStatus === 'pending'">
          <div class="icon-container pending-icon">
            <svg-icon icon-class="clock-yellow" />
          </div>
          <h1>
            {{ $t('ads_top_up_pending_title') }}
          </h1>
          <p>
            {{ $t('ads_top_up_pending_desc') }}
          </p>
          <router-link to="/ads/postpaid/invoice" class="w-full my-3" replace>
            <AdsButton class="w-full" variant="primary">
              {{ $t('ads_billings_view_postpaid_invoices') }}
            </AdsButton>
          </router-link>
          <router-link to="/ads/dashboard" class="w-full" replace>
            <AdsButton class="w-full" variant="outlined">
              {{ $t('ads_postpaid_success_to_dashboard') }}
            </AdsButton>
          </router-link>
        </template>
      </template>
    </main>
  </div>
</template>

<style lang="scss">
.ads-postpaid-response {
  .icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    margin-block-end: 20px;
    border-radius: 100%;

    svg {
      width: 40px;
      height: 40px;
    }
  }
}
</style>

<style lang="scss" scoped>
main {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 522px;
  padding: 48px;
  text-align: center;
  background: #fff;
  border-radius: 20px;
}

.ads-postpaid-response {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  // Due to body hav zoom: 90%
  // => we have to set min-height to 111.11vh to cover entire screen height
  min-height: 111.11vh;
  background: #0009;
}

.pending-icon {
  background: var(--warning-subtle);
}

.inline-cta-container {
  display: flex;
  gap: 12px;
  width: 100%;
}

.payment-processing {
  i {
    margin-block-end: 20px;
    font-size: 64px;
    color: #ab8dff;
  }
}
</style>
